/**
 * LEDGER DE XP de FamiDex — queries/mutations sobre `xp_event` [L].
 *
 * El ledger es append-only y SERVER-AUTHORITATIVE: el cliente encola una acción
 * (con la XP de la matriz como pista optimista para feedback instantáneo), pero
 * el SERVIDOR re-estampa la cantidad desde `XP_MATRIX` al procesar el batch
 * (antifraude — ver `core-rust/src/db/sync.rs`). La XP acumulada se DERIVA
 * sumando el ledger; el rango sale de `rankProgress` (reducer puro).
 */
import { asc, eq, type PowerSyncDrizzleDB, xp_event } from "@famidex/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";
import { xpForAction, type XpAction } from "./famidex-ranks";

/** Eventos de XP del usuario, en orden cronológico ascendente. */
export function getXpEventsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      id: xp_event.id,
      action: xp_event.action,
      xp: xp_event.xp,
      source_id: xp_event.source_id,
      created_at: xp_event.created_at,
    })
    .from(xp_event)
    .where(eq(xp_event.user_id, userId))
    .orderBy(asc(xp_event.created_at));
}

/** Fila mínima del ledger que la suma necesita. */
export interface XpLedgerRow {
  xp: number | null;
}

/** XP acumulada = suma del ledger. Ignora filas sin cantidad. Pura. */
export function sumXp(rows: readonly XpLedgerRow[]): number {
  let total = 0;
  for (const row of rows) {
    const value = row.xp ?? 0;
    if (Number.isFinite(value) && value > 0) {
      total += Math.floor(value);
    }
  }
  return total;
}

export interface RecordXpResult {
  id: string;
  /** XP optimista estampada por el cliente (el servidor la re-aplica). */
  xp: number;
}

/**
 * Registra una acción en el ledger (append-only). La `xp` que escribe el cliente
 * es sólo una PISTA optimista tomada de la matriz para feedback instantáneo: el
 * write-path del servidor la sobrescribe con `XP_MATRIX[action]` estampando el
 * `user_id` autenticado. Nunca actualiza filas existentes (el ledger no muta).
 */
export async function recordXpEventMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  action: XpAction,
  sourceId?: string | null,
): Promise<RecordXpResult> {
  const id = generateUUID();
  const xp = xpForAction(action);
  await db.insert(xp_event).values({
    id,
    user_id: userId,
    action,
    xp,
    source_id: sourceId ?? null,
    created_at: nowISO(),
  });
  return { id, xp };
}
